import { Routes } from '@angular/router';
import { authGuard } from './core/guards/auth.guard';
import { defaultRouteGuard } from './core/guards/default-route.guard';
import { MODULES, FUNCTIONS } from './shared/models/const.models';

export const routes: Routes = [
  {
    path: 'login',
    loadComponent: () =>
      import('./features/login/pages/login/login.component').then(
        (m) => m.LoginComponent,
      ),
  },
  {
    path: 'reset-password',
    loadComponent: () =>
      import('./features/reset-password/pages/reset-password.component').then(
        (m) => m.ResetPasswordComponent,
      ),
  },
  {
    path: 'set-new-password',
    loadComponent: () =>
      import('./features/set-new-password/pages/set-new-password.component').then(
        (m) => m.SetNewPasswordComponent,
      ),
  },
  {
    path: 'token-login',
    loadComponent: () =>
      import('./features/token-login/pages/token-login.component').then(
        (m) => m.TokenLoginComponent,
      ),
  },
  {
    path: 'info',
    loadComponent: () =>
      import('./features/info/pages/info/info.component').then(
        (m) => m.InfoPageComponent,
      ),
  },
  {
    path: '',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./core/pages/shell/shell.component').then(
        (m) => m.ShellComponent,
      ),
    children: [
      {
        path: '',
        pathMatch: 'full',
        canActivate: [defaultRouteGuard],
        children: [],
      },
      {
        path: 'rooms',
        canActivate: [authGuard],
        data: {
          roles: [FUNCTIONS.MANAGE_ROOMS, FUNCTIONS.SECOND_VERIFICATION],
          modules: [MODULES.ROOMS],
        },
        loadComponent: () =>
          import('./features/rooms/pages/rooms.component').then(
            (m) => m.RoomsComponent,
          ),
      },
      {
        path: 'kitchen',
        canActivate: [authGuard],
        data: {
          roles: [FUNCTIONS.MANAGE_KITCHEN, FUNCTIONS.CLEAN_KITCHEN],
          modules: [MODULES.KITCHEN],
        },
        loadComponent: () =>
          import('./features/kitchens/pages/kitchen.component').then(
            (m) => m.KitchenComponent,
          ),
      },
      {
        path: 'clean-order',
        canActivate: [authGuard],
        data: {
          roles: [FUNCTIONS.FIRST_VERIFICATION],
          modules: [MODULES.ROOMS],
        },
        loadComponent: () =>
          import('./features/clean-order/pages/clean-order.component').then(
            (m) => m.CleanOrderComponent,
          ),
      },
      {
        path: 'hotel-config',
        canActivate: [authGuard],
        data: {
          roles: [FUNCTIONS.SUPERADMIN],
        },
        loadComponent: () =>
          import('./features/hotel-config/pages/hotel-config/hotel-config.component').then(
            (m) => m.HotelConfigComponent,
          ),
      },
      {
        path: 'config-flow',
        canActivate: [authGuard],
        data: {
          roles: [FUNCTIONS.SUPERADMIN],
        },
        loadComponent: () =>
          import('./features/configure-flow/pages/configure-flow.component').then(
            (m) => m.ConfigureFlowComponent,
          ),
      },
      {
        path: 'role-config',
        canActivate: [authGuard],
        data: {
          roles: [FUNCTIONS.SUPERADMIN],
        },
        loadComponent: () =>
          import('./features/role-config/pages/role-config.component').then(
            (m) => m.RoleConfigComponent,
          ),
      },
      {
        path: 'my-rooms',
        canActivate: [authGuard],
        data: {
          roles: [FUNCTIONS.CLEANER],
          modules: [MODULES.ROOMS],
        },
        loadComponent: () =>
          import('./features/my-rooms/pages/my-rooms.component').then(
            (m) => m.MyRoomsComponent,
          ),
      },
      {
        path: 'hk-report',
        canActivate: [authGuard],
        data: {
          roles: [FUNCTIONS.UPLOAD_HK_REPORT],
          modules: [MODULES.ROOMS, MODULES.KITCHEN],
        },
        loadComponent: () =>
          import('./features/hk-report/pages/hk-report.component').then(
            (m) => m.HkReportComponent,
          ),
      },
      {
        path: 'assign-room',
        canActivate: [authGuard],
        data: {
          roles: [FUNCTIONS.ASSIGN_CLEANERS],
          modules: [MODULES.ROOMS],
        },
        loadComponent: () =>
          import('./features/assign-room/pages/assign-room.component').then(
            (m) => m.AssignRoomComponent,
          ),
      },
      {
        path: 'maintenance',
        canActivate: [authGuard],
        data: {
          roles: [FUNCTIONS.MANAGE_MAINTENANCE],
          modules: [MODULES.MAINTENANCE],
        },
        loadComponent: () =>
          import('./features/maintenance/pages/maintenance.component').then(
            (m) => m.MaintenanceComponent,
          ),
      },
      {
        path: 'my-maintenance',
        canActivate: [authGuard],
        data: {
          roles: [FUNCTIONS.MAINTENANCE],
          modules: [MODULES.MAINTENANCE],
        },
        loadComponent: () =>
          import('./features/my-maintenance/pages/my-maintenance.component').then(
            (m) => m.MyMaintenanceComponent,
          ),
      },
      {
        path: 'stats',
        canActivate: [authGuard],
        data: {
          roles: [
            FUNCTIONS.VIEW_CLEANING_STATS,
            FUNCTIONS.VIEW_KITCHEN_STATS,
            FUNCTIONS.VIEW_MAINTENANCE_STATS,
            FUNCTIONS.VIEW_ROOM_STATS,
          ],
          modules: [MODULES.STATS],
        },
        loadComponent: () =>
          import('./features/stats/pages/stats.component').then(
            (m) => m.StatsComponent,
          ),
      },
      {
        path: 'tasks',
        canActivate: [authGuard],
        data: {
          roles: [
            FUNCTIONS.MANAGE_TASKS,
          ],
          modules: [MODULES.TASKS],
        },
        loadComponent: () =>
          import('./features/tasks/pages/tasks.component').then(
            (m) => m.TasksComponent,
          ),
      },
      {
        path: 'my-tasks',
        canActivate: [authGuard],
        data: {
          roles: [FUNCTIONS.MY_TASKS],
          modules: [MODULES.TASKS],
        },
        loadComponent: () =>
          import('./features/my-tasks/pages/my-tasks.component').then(
            (m) => m.MyTasksComponent,
          ),
      },
      {
        path: 'daily-report',
        canActivate: [authGuard],
        data: {
          roles: [
            FUNCTIONS.VIEW_DAILY_REPORT,
          ],
          modules: [MODULES.DAILY_REPORT],
        },
        loadComponent: () =>
          import('./features/daily-report/pages/daily-report.component').then(
            (m) => m.DailyReportComponent,
          ),
      },
      {
        path: 'users',
        canActivate: [authGuard],
        data: {
          roles: [FUNCTIONS.ADMIN, FUNCTIONS.SUPERADMIN],
        },
        loadComponent: () =>
          import('./features/users/pages/users.component').then(
            (m) => m.UsersComponent,
          ),
      },
      {
        path: 'cleaners',
        canActivate: [authGuard],
        data: {
          roles: [FUNCTIONS.MANAGE_CLEANERS],
          modules: [MODULES.ROOMS, MODULES.KITCHEN],
        },
        loadComponent: () =>
          import('./features/cleaners/pages/cleaners.component').then(
            (m) => m.CleanersComponent,
          ),
      },
      {
        path: 'help',
        canActivate: [authGuard],
        data: {
          roles: ['TODO'],
        },
        loadComponent: () =>
          import('./features/help-page/page/help-page.component').then(
            (m) => m.HelpPageComponent,
          ),
      },
      {
        path: '**',
        canActivate: [defaultRouteGuard],
        children: [],
      },
    ],
  },
  {
    path: '**',
    redirectTo: '',
  },
];
